import { ChatMessage, RequiresAction } from '../../api/bookingAgent';

/**
 * U1-2396 deposit-card gate.
 *
 * The chat POST and the session GET already return `requires_action` /
 * `pending_requires_action`, `booking_state`, `quick_replies`, and (on the
 * session) `client`. They do not yet have a dedicated verified/slot contract.
 * A companion backend change may add the fields below. When they are present
 * we honour them. When they are absent we still refuse the pay card unless
 * this transcript shows the customer is verified and a time was chosen.
 *
 * Treated as verified when any of these is true:
 * - booking_state.customer_verified, identity_verified, client_verified,
 *   otp_verified, verified, or is_verified
 * - the same booleans on the session/chat payload (passed as customerVerified)
 * - booking_state.client_id, customer_id, or verified_client_id
 * - session `client` (sessionClientId)
 * - a user line matching "OTP verified" (the resume message this widget sends)
 * - identityVerifiedLocally, set after a successful OTP on this page
 *
 * The signed-in widget client id is not verified on its own. It counts only
 * once the server has linked a client (session `client` or a booking_state
 * client id) or OTP completed on this page / the resume line. A linked
 * server client still counts when its id differs from the storefront
 * attribute. Copying the widget id into the session client before that
 * link must not open the pay card.
 *
 * Treated as not verified when a verified flag is false, or pending_step /
 * next_step / awaiting (booking_state or the pendingStep argument) is an
 * identity step: identity, verify, verification, otp, otp_input.
 *
 * A slot is chosen when booking_state has a truthy slot_selected, has_slot,
 * slot_chosen, or time_chosen flag, or a filled slot, slot_id, selected_slot,
 * held_slot, chosen_slot, start, start_dt, start_time, appointment_start,
 * appointment_start_dt, booking_start, or selected_start. An explicit false
 * flag means no slot. requires_action.start_dt counts only when the state
 * does not say otherwise. Otherwise only a user message that confirms a slot
 * counts: "works", "book it", or a quick-reply time pick. Assistant lines and
 * the initial availability ask do not count.
 *
 * A deposit_payment action is dropped when booking_state.deposit_paid or
 * booking_state.booking_id is set, so a stale pending action cannot bring
 * the card back after payment.
 */

export const IDENTITY_PROMPT = 'Before I confirm, I need to verify your identity.';

export const IDENTITY_QUICK_REPLIES = ['Returning customer', 'New here'];

const VERIFIED_KEYS = [
  'customer_verified',
  'identity_verified',
  'client_verified',
  'otp_verified',
  'verified',
  'is_verified',
];

const CLIENT_ID_KEYS = ['client_id', 'customer_id', 'verified_client_id'];

const PENDING_STEP_KEYS = ['pending_step', 'next_step', 'awaiting'];

const IDENTITY_STEPS = new Set([
  'identity',
  'identify',
  'verification',
  'verify',
  'verify_identity',
  'otp',
  'otp_input',
  'customer_verification',
  'awaiting_otp',
  'awaiting_identity',
]);

const SLOT_FLAG_KEYS = ['slot_selected', 'has_slot', 'slot_chosen', 'time_chosen'];

const SLOT_VALUE_KEYS = [
  'slot',
  'slot_id',
  'selected_slot',
  'held_slot',
  'chosen_slot',
  'start',
  'start_dt',
  'start_time',
  'appointment_start',
  'appointment_start_dt',
  'booking_start',
  'selected_start',
];

const TIME_PATTERN = /\b\d{1,2}(?::\d{2})?\s*(a\.?m\.?|p\.?m\.?)\b/i;

const AVAILABILITY_ASK_PATTERN = /\b(?:i'?d like|i would like|available|availability|anything|how about|do you have|can i|looking|want|need|check|book)\b/i;

export interface DepositGateContext {
  bookingState?: Record<string, any> | null;
  sessionClientId?: string | null;
  signedInClientId?: string | null;
  identityVerifiedLocally?: boolean;
  pendingStep?: unknown;
  customerVerified?: unknown;
}

export function linkedClientId(bookingState?: Record<string, any> | null): string | null {
  if (!bookingState) {
    return null;
  }
  for (let index = 0; index < CLIENT_ID_KEYS.length; index += 1) {
    const value = nonEmptyId(bookingState[CLIENT_ID_KEYS[index]]);
    if (value) {
      return value;
    }
  }
  return null;
}

export function isCustomerVerified(messages: ChatMessage[], context: DepositGateContext): boolean {
  const step = readPendingStep(context);
  if (IDENTITY_STEPS.has(step)) {
    return false;
  }
  const explicit = readVerifiedFlag(context);
  if (explicit === false) {
    return false;
  }
  if (explicit === true) {
    return true;
  }
  if (context.identityVerifiedLocally) {
    return true;
  }
  if (hasLinkedServerClient(context)) {
    return true;
  }
  return messages.some((message) => (
    message.role === 'user' && /otp verified\b/i.test(message.content || '')
  ));
}

function hasLinkedServerClient(context: DepositGateContext): boolean {
  return Boolean(
    nonEmptyId(context.sessionClientId) || linkedClientId(context.bookingState),
  );
}

export function isSlotChosen(
  messages: ChatMessage[],
  context: DepositGateContext,
  action?: RequiresAction | null,
): boolean {
  const fromState = readSlotState(context.bookingState);
  if (fromState === 'chosen') {
    return true;
  }
  if (fromState === 'missing') {
    return false;
  }
  if (action?.start_dt && String(action.start_dt).trim()) {
    return true;
  }
  return messages.some((message) => userConfirmedSlot(message));
}

function userConfirmedSlot(message: ChatMessage): boolean {
  if (message.role !== 'user') {
    return false;
  }
  const text = (message.content || '').trim();
  if (!text || text.includes('?')) {
    return false;
  }
  if (/\bbook it\b/i.test(text)) {
    return true;
  }
  if (/\bworks\b/i.test(text) && (TIME_PATTERN.test(text) || isShortWorksReply(text))) {
    return true;
  }
  return isQuickReplyTimePick(text);
}

function isShortWorksReply(text: string): boolean {
  return /^(?:yes[, ]*)?(?:that |this |it )?(?:time )?works\.?$/i.test(text);
}

function isQuickReplyTimePick(text: string): boolean {
  if (text.includes('?') || !TIME_PATTERN.test(text)) {
    return false;
  }
  if (AVAILABILITY_ASK_PATTERN.test(text)) {
    return false;
  }
  const remainder = text
    .replace(TIME_PATTERN, ' ')
    .replace(/\b(?:sun|mon|tue|wed|thu|fri|sat|sunday|monday|tuesday|wednesday|thursday|friday|saturday)\b/gi, ' ')
    .replace(/\b(?:jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec|january|february|march|april|june|july|august|september|october|november|december)\b/gi, ' ')
    .replace(/\b\d{1,2}(?:st|nd|rd|th)?\b/g, ' ')
    .replace(/\b(?:at|on|the)\b/gi, ' ')
    .replace(/[^a-z0-9]+/gi, ' ')
    .trim();
  return remainder.length === 0;
}

export function clearPaidDepositAction(
  action: RequiresAction | null | undefined,
  bookingState?: Record<string, any> | null,
): RequiresAction | null {
  if (!action) {
    return null;
  }
  if (action.type === 'deposit_payment' && (bookingState?.deposit_paid || bookingState?.booking_id)) {
    return null;
  }
  return action;
}

export function gateRequiresAction(
  action: RequiresAction | null | undefined,
  messages: ChatMessage[],
  context: DepositGateContext,
): RequiresAction | null {
  const openAction = clearPaidDepositAction(action, context.bookingState);
  if (!openAction) {
    return null;
  }
  if (openAction.type !== 'deposit_payment') {
    return openAction;
  }
  if (!canShowDepositCard(messages, context, openAction)) {
    return null;
  }
  return openAction;
}

export function restoreChatTranscript(
  messages: ChatMessage[],
  context: DepositGateContext,
  action?: RequiresAction | null,
): ChatMessage[] {
  if (action && action.type !== 'deposit_payment') {
    return messages;
  }
  if (isCustomerVerified(messages, context)) {
    return messages;
  }
  if (isPriceOnlyQuestion(latestUserText(messages))) {
    return messages;
  }
  if (!isSlotChosen(messages, context, action)) {
    return messages;
  }
  if (messages.some((message) => messageHasIdentityChoice(message))) {
    return messages;
  }
  if (!messages.length) {
    return messages;
  }
  const lastIndex = messages.length - 1;
  const last = messages[lastIndex];
  if (last.role !== 'assistant') {
    return messages;
  }
  if ((last.quick_replies || []).length > 0) {
    return messages;
  }
  if (isIdentityAsk(last.content) || last.content.trim() === IDENTITY_PROMPT) {
    return messages.map((message, index) => (
      index === lastIndex
        ? { ...message, quick_replies: IDENTITY_QUICK_REPLIES }
        : message
    ));
  }
  if (isBookItPrompt(last.content)) {
    return [
      ...messages,
      {
        role: 'assistant',
        content: IDENTITY_PROMPT,
        quick_replies: IDENTITY_QUICK_REPLIES,
      },
    ];
  }
  return messages;
}

export function applyAgentTurn(
  messages: ChatMessage[],
  action: RequiresAction | null | undefined,
  context: DepositGateContext,
): { messages: ChatMessage[]; requiresAction: RequiresAction | null } {
  // Gate on the pre-restore messages; the UI renders the restored transcript.
  return {
    messages: restoreChatTranscript(messages, context, action),
    requiresAction: gateRequiresAction(action, messages, context),
  };
}

function canShowDepositCard(
  messages: ChatMessage[],
  context: DepositGateContext,
  action: RequiresAction,
): boolean {
  if (isPriceOnlyQuestion(latestUserText(messages))) {
    return false;
  }
  const lastAssistant = lastAssistantMessage(messages);
  if (lastAssistant && messageHasIdentityChoice(lastAssistant)) {
    return false;
  }
  if (!isSlotChosen(messages, context, action)) {
    return false;
  }
  return isCustomerVerified(messages, context);
}

function isPriceOnlyQuestion(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) {
    return false;
  }
  const asksPrice = /\b(how much|what(?:'s| is) the (?:deposit|price|cost)|deposit (?:for|amount|cost)|price|cost)\b/i.test(trimmed);
  const confirmsTime = TIME_PATTERN.test(trimmed) || /\b(book it|works)\b/i.test(trimmed);
  return asksPrice && !confirmsTime;
}

function latestUserText(messages: ChatMessage[]): string {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index].role === 'user' && (messages[index].content || '').trim()) {
      return messages[index].content;
    }
  }
  return '';
}

function lastAssistantMessage(messages: ChatMessage[]): ChatMessage | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index].role === 'assistant') {
      return messages[index];
    }
  }
  return null;
}

function messageHasIdentityChoice(message: ChatMessage): boolean {
  return (message.quick_replies || []).some((option) => {
    const label = typeof option === 'string' ? option : (option.display || option.value || '');
    return isIdentityQuickReplyLabel(label);
  });
}

function isIdentityQuickReplyLabel(label: string): boolean {
  const text = label.trim().toLowerCase();
  if (!text) {
    return false;
  }
  if (text.includes('returning')) {
    return true;
  }
  return text === 'new here'
    || text === 'new customer'
    || text === "i'm new"
    || text === 'im new'
    || text === 'i am new';
}

function isIdentityAsk(content: string): boolean {
  return /verify your identity|verify your (?:mobile|number|phone)|identity verification|confirm your identity/i.test(content || '');
}

function isBookItPrompt(content: string): boolean {
  return /\bbook it\b/i.test(content || '');
}

function readPendingStep(context: DepositGateContext): string {
  const direct = normalizeStep(context.pendingStep);
  if (direct) {
    return direct;
  }
  const bookingState = context.bookingState;
  if (!bookingState) {
    return '';
  }
  for (let index = 0; index < PENDING_STEP_KEYS.length; index += 1) {
    const step = normalizeStep(bookingState[PENDING_STEP_KEYS[index]]);
    if (step) {
      return step;
    }
  }
  return '';
}

function readVerifiedFlag(context: DepositGateContext): boolean | undefined {
  const topLevel = readBoolean(context.customerVerified);
  if (topLevel !== undefined) {
    return topLevel;
  }
  const bookingState = context.bookingState;
  if (!bookingState) {
    return undefined;
  }
  for (let index = 0; index < VERIFIED_KEYS.length; index += 1) {
    const key = VERIFIED_KEYS[index];
    if (Object.prototype.hasOwnProperty.call(bookingState, key)) {
      const parsed = readBoolean(bookingState[key]);
      if (parsed !== undefined) {
        return parsed;
      }
    }
  }
  return undefined;
}

function readSlotState(bookingState?: Record<string, any> | null): 'chosen' | 'missing' | 'unset' {
  if (!bookingState) {
    return 'unset';
  }
  let sawTrue = false;
  let sawFalse = false;
  for (let index = 0; index < SLOT_FLAG_KEYS.length; index += 1) {
    const key = SLOT_FLAG_KEYS[index];
    if (!Object.prototype.hasOwnProperty.call(bookingState, key)) {
      continue;
    }
    const parsed = readBoolean(bookingState[key]);
    if (parsed === true) {
      sawTrue = true;
    }
    if (parsed === false) {
      sawFalse = true;
    }
  }
  if (sawTrue) {
    return 'chosen';
  }
  if (sawFalse) {
    return 'missing';
  }
  let sawValue = false;
  for (let index = 0; index < SLOT_VALUE_KEYS.length; index += 1) {
    const key = SLOT_VALUE_KEYS[index];
    if (!Object.prototype.hasOwnProperty.call(bookingState, key)) {
      continue;
    }
    sawValue = true;
    if (isFilledSlot(bookingState[key])) {
      return 'chosen';
    }
  }
  return sawValue ? 'missing' : 'unset';
}

function isFilledSlot(value: unknown): boolean {
  if (value === null || value === undefined || value === false) {
    return false;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).some((entry) => isFilledSlot(entry));
  }
  return false;
}

function readBoolean(value: unknown): boolean | undefined {
  if (value === true || value === 'true' || value === 1) {
    return true;
  }
  if (value === false || value === 'false' || value === 0) {
    return false;
  }
  return undefined;
}

function normalizeStep(value: unknown): string {
  if (typeof value !== 'string') {
    return '';
  }
  return value.trim().toLowerCase().replace(/[\s-]+/g, '_');
}

function nonEmptyId(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  return null;
}
